import React, { useState } from 'react';
import { Shield, Lock, Eye, Plus, Edit2, Trash2, Save, X, AlertCircle } from 'lucide-react';
import { RBAC_MATRIX } from '../config/roles';

interface Role {
  name: string;
  permissions: Record<string, string[]>;
}

interface RBACState {
  roles: Record<string, Role>;
}

const RBACManagement: React.FC = () => {
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [editingResource, setEditingResource] = useState<string | null>(null);
  const [editingActions, setEditingActions] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Get all resources and actions from RBAC_MATRIX
  const allResources = Object.values(RBAC_MATRIX).reduce((acc, role) => {
    Object.keys(role.resources).forEach(resource => {
      if (!acc.includes(resource)) acc.push(resource);
    });
    return acc;
  }, [] as string[]).sort();

  const allActions = ['view', 'create', 'edit', 'delete', 'manage', 'export', 'import'];

  const roles = Object.keys(RBAC_MATRIX).sort();
  const currentRole = selectedRole ? (RBAC_MATRIX as any)[selectedRole] : null;

  // Filter resources based on search
  const filteredResources = allResources.filter(resource =>
    resource.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEditResource = (resource: string) => {
    setEditingResource(resource);
    const actions = currentRole?.resources[resource]?.actions || [];
    setEditingActions([...actions]);
  };

  const toggleAction = (action: string) => {
    setEditingActions(prev =>
      prev.includes(action)
        ? prev.filter(a => a !== action)
        : [...prev, action]
    );
  };

  const getRoleColor = (role: string) => {
    const colors: Record<string, string> = {
      superadmin: 'bg-red-100 text-red-800 border-red-300',
      admin: 'bg-orange-100 text-orange-800 border-orange-300',
      teknisyen: 'bg-primary/20 text-primary-dark border-primary/50',
      calisan: 'bg-green-100 text-green-800 border-green-300',
    };
    return colors[role] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600';
  };

  const getRoleIcon = (role: string) => {
    const icons: Record<string, React.ReactNode> = {
      superadmin: <Shield className="w-4 h-4" />,
      admin: <Lock className="w-4 h-4" />,
      teknisyen: <Eye className="w-4 h-4" />,
      calisan: <Eye className="w-4 h-4" />,
    };
    return icons[role] || <Shield className="w-4 h-4" />;
  };

  const getPermissionStats = (role: string) => {
    const roleData = (RBAC_MATRIX as any)[role];
    let totalResources = 0;
    let totalPermissions = 0;

    if (roleData?.resources) {
      totalResources = Object.keys(roleData.resources).length;
      totalPermissions = Object.values(roleData.resources).reduce(
        (sum: number, resource: any) => sum + (resource.actions?.length || 0),
        0
      );
    }

    return { totalResources, totalPermissions };
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-700 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">RBAC Yönetimi</h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            Rol Tabanlı Erişim Kontrol matrisini görüntüleyin ve yönetin
          </p>
        </div>

        {/* Alert */}
        <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 mb-6 flex gap-3">
          <AlertCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <div className="text-sm text-primary-dark">
            <p className="font-medium mb-1">Not: Bu arayüz salt okunurdur</p>
            <p>RBAC matrisini düzenlemek için kaynak kodda <code className="bg-primary/20 px-2 py-1 rounded">src/config/roles.ts</code> dosyasını güncelleyin</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar - Roles */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="font-semibold text-gray-900 dark:text-gray-100">Roller</h2>
              </div>
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {roles.map((role) => {
                  const stats = getPermissionStats(role);
                  return (
                    <button
                      key={role}
                      onClick={() => setSelectedRole(role)}
                      className={`w-full text-left p-4 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition ${
                        selectedRole === role ? 'bg-primary/10 border-l-4 border-primary' : ''
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        {getRoleIcon(role)}
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleColor(role)}`}>
                          {role}
                        </span>
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        <p>📁 {stats.totalResources} kaynak</p>
                        <p>🔑 {stats.totalPermissions} yetki</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {!selectedRole ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
                <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">Bir rol seçerek başlayın</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Role Header */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 capitalize">{selectedRole} Rolü</h2>
                      <p className="text-gray-600 dark:text-gray-400 mt-1">
                        {selectedRole === 'superadmin' && 'Tüm kaynaklara tam erişim'}
                        {selectedRole === 'admin' && 'İdari işlemler için yüksek seviye erişim'}
                        {selectedRole === 'teknisyen' && 'Teknik işlemler ve bakım için sınırlı erişim'}
                        {selectedRole === 'calisan' && 'Temel işlemler için minimal erişim'}
                      </p>
                    </div>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getRoleColor(selectedRole)}`}>
                      {getRoleIcon(selectedRole)}
                      <span className="ml-2">{selectedRole}</span>
                    </span>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-primary">
                        {getPermissionStats(selectedRole).totalResources}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Kaynak</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {getPermissionStats(selectedRole).totalPermissions}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Yetki</div>
                    </div>
                  </div>
                </div>

                {/* Search */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                  <input
                    type="text"
                    placeholder="Kaynak ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                {/* Resources & Permissions Table */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            Kaynak
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            Yetkiler
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {filteredResources.length === 0 ? (
                          <tr>
                            <td colSpan={2} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                              Sonuç bulunamadı
                            </td>
                          </tr>
                        ) : (
                          filteredResources.map((resource) => {
                            const permissions = currentRole?.resources[resource]?.actions || [];
                            return (
                              <tr key={resource} className="hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700">
                                <td className="px-6 py-4">
                                  <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono text-gray-900 dark:text-gray-100">
                                    {resource}
                                  </code>
                                </td>
                                <td className="px-6 py-4">
                                  {permissions.length === 0 ? (
                                    <span className="text-gray-500 dark:text-gray-400 text-sm">Erişim yok</span>
                                  ) : (
                                    <div className="flex flex-wrap gap-2">
                                      {permissions.map((action) => (
                                        <span
                                          key={action}
                                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-300"
                                        >
                                          {action}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* All Actions Reference */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Kullanılabilir Yetkiler</h3>
                  <div className="flex flex-wrap gap-2">
                    {allActions.map((action) => (
                      <span
                        key={action}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border border-gray-300 dark:border-gray-600"
                      >
                        {action}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center gap-3 mb-3">
              <Shield className="w-6 h-6 text-primary" />
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">Rol Nedir?</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Rol, kullanıcılara atanan ve belirli kaynaklar üzerinde yetkiler tanımlayan bir kimliktir.
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center gap-3 mb-3">
              <Lock className="w-6 h-6 text-green-600" />
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">Yetki Nedir?</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Yetki, bir kaynak üzerinde yapılabilecek belirli bir işlemi (view, create, edit, delete) tanımlar.
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center gap-3 mb-3">
              <Eye className="w-6 h-6 text-purple-600" />
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">Kaynak Nedir?</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Kaynak, uygulamada korunan bir bölümdür (cameras, orders, users vb.).
            </p>
          </div>
        </div>

        {/* Developer Note */}
        <div className="mt-8 bg-amber-50 border border-amber-200 rounded-lg p-6">
          <h3 className="font-semibold text-amber-900 mb-2">Geliştiriciler İçin</h3>
          <p className="text-sm text-amber-800 mb-3">
            RBAC matrisini değiştirmek için aşağıdaki dosyaları düzenleyin:
          </p>
          <ul className="text-sm text-amber-800 space-y-1">
            <li>📱 <code className="bg-amber-100 px-2 py-1 rounded font-mono">src/config/roles.ts</code> - Frontend RBAC matrisi</li>
            <li>🔧 <code className="bg-amber-100 px-2 py-1 rounded font-mono">backend/config/roles.js</code> - Backend RBAC matrisi</li>
            <li>🔗 İki dosya senkronize tutulmalı!</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default RBACManagement;
